import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileTextIcon, EditIcon, TrashIcon, EyeIcon, SearchIcon, FilterIcon, CalendarIcon, AlertCircleIcon, RefreshCwIcon } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { collection, query, where, orderBy, getDocs, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { Article, getScheduledArticles } from '../../services/articles';
const ArticlesList: React.FC = () => {
  const {
    user,
    hasRole
  } = useAuth();
  const [articles, setArticles] = useState<Article[]>([]);
  const [scheduledArticles, setScheduledArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'draft' | 'published' | 'archived' | 'scheduled'>('all');
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const fetchArticles = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Fetching articles for dashboard...');
      const articlesRef = collection(db, 'articles');
      let articlesQuery;
      // Super admins and editors can see all articles
      if (hasRole(['superadmin', 'editor'])) {
        console.log('Fetching all articles (admin/editor view)');
        articlesQuery = query(articlesRef, orderBy('updatedAt', 'desc'));
      } else {
        // Authors and contributors only see their own articles
        console.log(`Fetching articles for author: ${user?.uid}`);
        articlesQuery = query(articlesRef, where('author.id', '==', user?.uid), orderBy('updatedAt', 'desc'));
      }
      const snapshot = await getDocs(articlesQuery);
      console.log(`Found ${snapshot.docs.length} articles`);
      const articlesList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Article[];
      setArticles(articlesList);
      // Fetch scheduled articles separately
      if (hasRole(['superadmin', 'editor', 'author'])) {
        try {
          console.log('Fetching scheduled articles...');
          const scheduled = await getScheduledArticles();
          console.log(`Found ${scheduled.length} scheduled articles`);
          setScheduledArticles(scheduled);
        } catch (scheduledError) {
          console.error('Error fetching scheduled articles:', scheduledError);
          // Don't fail the whole component if just the scheduled articles fail
        }
      }
    } catch (err) {
      console.error('Error fetching articles:', err);
      setError('Failed to load articles. Please try again. Error: ' + (err instanceof Error ? err.message : String(err)));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchArticles();
  }, [user, hasRole]);
  const handleDeleteArticle = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'articles', id));
      setArticles(articles.filter(article => article.id !== id));
      setScheduledArticles(scheduledArticles.filter(article => article.id !== id));
      setConfirmDelete(null);
    } catch (err) {
      console.error('Error deleting article:', err);
      setError('Failed to delete article. Please try again.');
    }
  };
  // Format date
  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Date unavailable';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  // Filter articles
  const filteredArticles = articles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchTerm.toLowerCase()) || article.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    // For scheduled filter, check if article is published and has a future scheduledFor date
    const isScheduled = article.status === 'published' && article.scheduledFor && article.scheduledFor.toDate() > new Date();
    const matchesStatus = statusFilter === 'all' ? true : statusFilter === 'scheduled' ? isScheduled : article.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  return <div data-id="element-191">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6" data-id="element-192">
        <h1 className="text-2xl font-bold text-navy mb-4 md:mb-0 font-montserrat" data-id="element-193">
          Manage Articles
        </h1>
        <div className="flex gap-2" data-id="element-194">
          {error && <button onClick={fetchArticles} className="btn-secondary flex items-center" title="Retry loading articles" data-id="element-195">
              <RefreshCwIcon size={18} className="mr-2" data-id="element-196" />
              Retry
            </button>}
          <Link to="/dashboard/articles/new" className="btn-primary inline-flex items-center" data-id="element-197">
            <FileTextIcon size={18} className="mr-2" data-id="element-198" />
            Create New Article
          </Link>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6" data-id="element-199">
        <div className="flex flex-col md:flex-row gap-4" data-id="element-200">
          <div className="flex-1" data-id="element-201">
            <div className="relative" data-id="element-202">
              <input type="text" placeholder="Search articles..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-203" />
              <SearchIcon size={18} className="absolute left-3 top-2.5 text-gray-400" data-id="element-204" />
            </div>
          </div>
          <div className="w-full md:w-48" data-id="element-205">
            <div className="relative" data-id="element-206">
              <select className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange appearance-none" value={statusFilter} onChange={e => setStatusFilter(e.target.value as any)} data-id="element-207">
                <option value="all" data-id="element-208">All Status</option>
                <option value="draft" data-id="element-209">Draft</option>
                <option value="published" data-id="element-210">Published</option>
                <option value="scheduled" data-id="element-211">Scheduled</option>
                <option value="archived" data-id="element-212">Archived</option>
              </select>
              <FilterIcon size={18} className="absolute left-3 top-2.5 text-gray-400" data-id="element-213" />
            </div>
          </div>
        </div>
      </div>

      {/* Scheduled Articles Section */}
      {scheduledArticles.length > 0 && statusFilter !== 'draft' && statusFilter !== 'archived' && <div className="bg-blue-50 rounded-lg shadow-sm p-6 mb-6" data-id="element-214">
            <h2 className="text-xl font-bold text-navy mb-4 font-montserrat flex items-center" data-id="element-215">
              <CalendarIcon size={20} className="mr-2" data-id="element-216" />
              Scheduled Articles ({scheduledArticles.length})
            </h2>
            <div className="overflow-x-auto" data-id="element-217">
              <table className="w-full" data-id="element-218">
                <thead className="bg-blue-100" data-id="element-219">
                  <tr data-id="element-220">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider" data-id="element-221">
                      Title
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider" data-id="element-222">
                      Scheduled For
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider" data-id="element-223">
                      Author
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider" data-id="element-224">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-blue-200" data-id="element-225">
                  {scheduledArticles.map(article => <tr key={article.id} className="hover:bg-blue-100" data-id="element-226">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-227">
                        <div className="text-sm font-medium text-navy" data-id="element-228">
                          {article.title}
                        </div>
                        <div className="text-sm text-gray-500 truncate max-w-xs" data-id="element-229">
                          {article.excerpt.substring(0, 60)}...
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-230">
                        {formatDate(article.scheduledFor)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-231">
                        {article.author.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-232">
                        <div className="flex justify-end space-x-2" data-id="element-233">
                          <Link to={`/dashboard/articles/edit/${article.id}`} className="text-blue-600 hover:text-blue-900" data-id="element-234">
                            <EditIcon size={18} data-id="element-235" />
                          </Link>
                          <button onClick={() => setConfirmDelete(article.id)} className="text-red-600 hover:text-red-900" data-id="element-236">
                            <TrashIcon size={18} data-id="element-237" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>}

      {loading ? <div className="flex justify-center py-12" data-id="element-238">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-navy" data-id="element-239"></div>
        </div> : error ? <div className="bg-red-50 text-red-700 p-6 rounded-md mb-6" data-id="element-240">
          <div className="flex items-start" data-id="element-241">
            <AlertCircleIcon size={24} className="mr-3 mt-0.5" data-id="element-242" />
            <div data-id="element-243">
              <h3 className="font-bold mb-2" data-id="element-244">Error Loading Articles</h3>
              <p className="mb-4" data-id="element-245">{error}</p>
              <p className="text-sm mb-4" data-id="element-246">
                This may be due to missing Firestore indexes. Check your
                Firebase console for required indexes.
              </p>
              <button onClick={fetchArticles} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center w-fit" data-id="element-247">
                <RefreshCwIcon size={16} className="mr-2" data-id="element-248" />
                Try Again
              </button>
            </div>
          </div>
        </div> : filteredArticles.length === 0 ? <div className="bg-white rounded-lg shadow-sm p-8 text-center" data-id="element-249">
          <FileTextIcon size={48} className="mx-auto text-gray-400 mb-4" data-id="element-250" />
          <h3 className="text-xl font-bold text-navy mb-2" data-id="element-251">
            No Articles Found
          </h3>
          <p className="text-gray-600 mb-6" data-id="element-252">
            {searchTerm || statusFilter !== 'all' ? 'No articles match your current filters.' : "You haven't created any articles yet."}
          </p>
          <Link to="/dashboard/articles/new" className="btn-primary" data-id="element-253">
            Create Your First Article
          </Link>
        </div> : <div className="bg-white rounded-lg shadow-sm overflow-hidden" data-id="element-254">
          <div className="overflow-x-auto" data-id="element-255">
            <table className="w-full" data-id="element-256">
              <thead className="bg-gray-50" data-id="element-257">
                <tr data-id="element-258">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-259">
                    Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-260">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-261">
                    Last Updated
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-262">
                    Author
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-263">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200" data-id="element-264">
                {filteredArticles.map(article => {
              // Check if article is scheduled
              const isScheduled = article.status === 'published' && article.scheduledFor && article.scheduledFor.toDate() > new Date();
              return <tr key={article.id} className="hover:bg-gray-50" data-id="element-265">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-266">
                        <div className="text-sm font-medium text-navy" data-id="element-267">
                          {article.title}
                        </div>
                        <div className="text-sm text-gray-500 truncate max-w-xs" data-id="element-268">
                          {article.excerpt.substring(0, 60)}...
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-269">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${isScheduled ? 'bg-blue-100 text-blue-800' : article.status === 'published' ? 'bg-green-100 text-green-800' : article.status === 'draft' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-270">
                          {isScheduled ? 'Scheduled' : article.status.charAt(0).toUpperCase() + article.status.slice(1)}
                        </span>
                        {isScheduled && <div className="text-xs text-gray-500 mt-1" data-id="element-271">
                            {formatDate(article.scheduledFor)}
                          </div>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-272">
                        {formatDate(article.updatedAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-273">
                        {article.author.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-274">
                        <div className="flex justify-end space-x-2" data-id="element-275">
                          {article.status === 'published' && !isScheduled && <Link to={`/enews/${article.id}`} className="text-indigo-600 hover:text-indigo-900" target="_blank" data-id="element-276">
                              <EyeIcon size={18} data-id="element-277" />
                            </Link>}
                          <Link to={`/dashboard/articles/edit/${article.id}`} className="text-blue-600 hover:text-blue-900" data-id="element-278">
                            <EditIcon size={18} data-id="element-279" />
                          </Link>
                          <button onClick={() => setConfirmDelete(article.id)} className="text-red-600 hover:text-red-900" data-id="element-280">
                            <TrashIcon size={18} data-id="element-281" />
                          </button>
                        </div>
                      </td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div>
        </div>}

      {/* Delete Confirmation Modal */}
      {confirmDelete && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-282">
          <div className="bg-white rounded-lg p-6 max-w-md w-full" data-id="element-283">
            <h3 className="text-xl font-bold text-navy mb-4" data-id="element-284">Confirm Delete</h3>
            <p className="mb-6" data-id="element-285">
              Are you sure you want to delete this article? This action cannot
              be undone.
            </p>
            <div className="flex justify-end space-x-3" data-id="element-286">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-287">
                Cancel
              </button>
              <button onClick={() => handleDeleteArticle(confirmDelete)} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700" data-id="element-288">
                Delete
              </button>
            </div>
          </div>
        </div>}
    </div>;
};
export default ArticlesList;